// @ts-strict-ignore
import { getActionServiceAttribute } from '../thresholds';
import type {
  Action,
  DataRoute,
  SchemaInstance,
  WorkflowProps,
} from '../../store/engine/types';
import { extractValuesByKey } from '../util';
import type { DataRoutingCategories } from './types';

export function extractDataRouteIds(
  expression: SchemaInstance['actions'][string]['output']['expression'],
): string[] {
  const DATA_ROUTE_ID_KEY = 'data_route_id';
  const dataRouteIds = extractValuesByKey(expression, DATA_ROUTE_ID_KEY);
  return dataRouteIds;
}


export function isDataRouteAnMLA(dataRoute: DataRoute): boolean {
  return dataRoute.attribute.multiLevelAttributeFilter?.filterComponents?.length > 0;
}

/**
 * Accepts a data route as it exists in state and populates it with the objects of various
 * things it might be referencing from other parts of the system, including
 * services, service attributes
 */
export function populateDataRouteData({
  actions,
  services,
  dataRoute,
}: {
  actions: SchemaInstance['actions'];
  services: WorkflowProps['services'];
  dataRoute: DataRoute;
}) {
  const { action, service, attribute } = getActionServiceAttribute({
    actions,
    actionId: dataRoute.sourceId,
    attributeId: dataRoute.attribute?.id,
    services,
  });

  return {
    ...dataRoute,
    sourceAction: action,
    attribute: {
      ...dataRoute.attribute,
      service,
      details: attribute,
    },
  };
}

export function getIncomingDataRoutingCategories({
  action,
  dataRoutes,
  services,
}: {
  action: Action;
  dataRoutes: SchemaInstance['dataRoutes'];
  services: WorkflowProps['services'];
}): DataRoutingCategories[] {
  const selectedActionCategories = services[action.service.internalName].categories;

  if (!selectedActionCategories.request) return null;
  return selectedActionCategories.request
    .map(category => {
      // Search for existing route that could override the current route's null source
      const definedDataRoute = Object.values(dataRoutes).find(route => (
        route.targetId === action.publicId && route.category.id === category.category_id
      ));

      return definedDataRoute ? {
        category,
        dataRoute: definedDataRoute,
      } : {
        category,
      };
    });
}

export function getOutgoingDataRoutingCategories({
  action,
  services,
  dataRoutes,
}: {
  action: Action;
  services: WorkflowProps['services'];
  dataRoutes: SchemaInstance['dataRoutes'];
}): DataRoutingCategories[] {
  const selectedActionCategories = services[action.service.internalName].categories;

  if (!selectedActionCategories.response) return null;
  return selectedActionCategories.response
    .map(category => {
      // Search for existing route that could override the current route's null source
      const definedDataRoute = Object.values(dataRoutes).find(route => (
        route.sourceId === action.publicId && route.category.id === category.id
      ));

      return definedDataRoute ? {
        category,
        dataRoute: definedDataRoute,
      } : {
        category,
      };
    });
}

export function getDataRoutesForAction(args: {
  action: Action;
  dataRoutes: Record<DataRoute['publicId'], DataRoute>;
}) {
  const { action, dataRoutes } = args;

  return Object.values(dataRoutes).filter(dataRoute => dataRoute.targetId === action.publicId);
}


export function indexDataRoutesByPublicId(dataRoutes: DataRoute[]) {
  return Object.fromEntries(
    dataRoutes.map(dataRoute => [dataRoute.publicId, dataRoute]),
  );
}
